import React, { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import UserCartSection from '../UserCartItems/UserCartSection'
import { getNumberOfActiveUserCartItems, getUserCartItemsArray, selectIsLuxPlusInUserCart } from 'selectors/userCart'
import useAllUserCartItemsMetadata from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { getCartItemDate } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { sortBy } from 'lib/array/arrayUtils'
import { getUserCartTotals } from 'lib/userCart/userCartTotals'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import styled from 'styled-components'
import { rem } from 'polished'
import { pluralizeToString } from 'lib/string/pluralize'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useFetchHotelItemData from 'hooks/UserCart/useFetchHotelItemData'
import useFetchExperienceItemData from 'hooks/UserCart/useFetchExperienceItemData'
import useFetchSubscriptionItemData from 'hooks/UserCart/useFetchSubscriptionItemData'
import cn from 'clsx'

// Hack solution around DropdownSheetBody padding to match designs
const NoHorizontalPadding = styled.div`
  margin: -${rem(24)};

  &.has-top-padding {
    margin-top: 0;
  }

  background-color: ${props => props.theme.palette.neutral.default.six};
`

interface Props {
  open: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
  hasDismissButton?: boolean
}

function UserCartDropdownPreview(props: Props) {
  const { open, onClose, buttonRef, hasDismissButton } = props

  const cartItems = useAppSelector(getUserCartItemsArray)
  const sortedCartItems = useMemo(() => sortBy(cartItems, getCartItemDate, 'asc'), [cartItems])

  const { fetching, error, items: cartItemsData } = useAllUserCartItemsMetadata(cartItems)

  useFetchHotelItemData()
  useFetchExperienceItemData()
  useFetchSubscriptionItemData()

  const loading = fetching && !error

  const numberOfActiveItems = useAppSelector(getNumberOfActiveUserCartItems)

  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const hasJoinInCart = useAppSelector(selectIsLuxPlusInUserCart)
  const hasLuxPlusBenefits = isLuxPlusMember || hasJoinInCart

  const { totalPrice } = useMemo(() => getUserCartTotals(cartItemsData, hasLuxPlusBenefits), [cartItemsData, hasLuxPlusBenefits])

  return (
    <DropdownSheet
      open={open}
      size="M"
      anchorRef={buttonRef}
      triggerRef={buttonRef}
      placement="bottom-end"
      onClose={onClose}
      hasDismissButton={!loading && hasDismissButton}
      anchorOffset={0}
      primaryActionProps={{
        children: `View cart (${pluralizeToString('item', numberOfActiveItems)})`,
        to: '/cart',
        onClick: onClose,
      }}
      footerStart={<>
        <LoadingIndicator visible={loading} inline floating opaque />
        <div>
          <Caption variant="medium" weight="bold">Cart total:</Caption>
          <BodyText variant="large" weight="bold"><FormatCurrency value={totalPrice} /></BodyText>
        </div>
      </>}
    >
      <NoHorizontalPadding className={cn({ 'has-top-padding': hasDismissButton })}>
        <UserCartSection
          items={sortedCartItems}
          cartItemsData={cartItemsData}
          tileVariant="condensed"
        />
      </NoHorizontalPadding>
    </DropdownSheet>
  )
}

export default UserCartDropdownPreview
